import { Component, Input } from '@angular/core';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { DeviceService } from '../../device/device.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { DeviceFactory } from '../../device/device-factory/device-factory';
import { CommonService } from 'src/app/shared/services/common.service';
import { DecimalPipe } from '@angular/common';

@Component({
	selector: 'app-daily-truck-usage-report',
	templateUrl: './daily-truck-usage-report.component.html',
	styleUrls: ['./daily-truck-usage-report.component.css']
})
export class DailyTruckUsageReportComponent {
	@Input() device: any = {}
	@Input() studyInfo: any = {}
	@Input() currentSite: any = {};

	maxDate = new Date((new Date()).getTime() - 24 * 60 * 60 * 1000); // yesterday
	toDate = this.maxDate; // yesterday
	fromDate = new Date((new Date()).getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
	minDate = this.fromDate;

	columnConfig: ColumnsConfig[] = [
		{key: 'day', name: this.translate.instant('g.date'), type: 'date'},
		{key: 'in_use_hours', name: this.translate.instant('truck_performance.in_use_hours'), type: 'number'},
		{key: 'total_discharged_ah', name: this.translate.instant('truck_performance.total_discharged_ah'), type: 'number'},
		{key: 'truck_energy_score', name: this.translate.instant('truck_performance.truck_energy_score'), type: 'number', hasTooltip: this.translate.instant('tooltip.usage_score_column')},
		{key: 'total_kwh_used', name: this.translate.instant('truck_performance.total_kwh_used'), type: 'number'},
		{key: 'total_in_use_hours', name: this.translate.instant('truck_performance.total_in_use_hours'), type: 'number'},
		{key: 'total_travel_hours', name: this.translate.instant('truck_performance.total_travel_hours'), type: 'number'},
		{key: 'total_lift_hours', name: this.translate.instant('truck_performance.total_lift_hours'), type: 'number'},
		{key: 'total_idle_hours', name: this.translate.instant('truck_performance.total_idle_hours'), type: 'number'},
		{key: 'total_off_charging_hours', name: this.translate.instant('truck_performance.total_off_charging_hours'), type: 'number'},
	];

	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 100,
		fitScreen: true,
		hideNoData: true,
		draggable: true,
		fileName: this.translate.instant('utilization.daily_truck_usage_report'),
		csvFileHeaderInfo: []
	};

	constructor(
		private deviceService: DeviceService,
		private translate: TranslateService,
		private commonService: CommonService,
	) {}

	ngOnInit() {
		if (this.device?.config_info?.installation_date)
			this.minDate = moment.utc(this.device.config_info.installation_date * 1000).startOf('day').toDate();

		if (this.studyInfo?.id) {
			const studyMinDate = new Date(this.studyInfo.start_study_time*1000);
			this.maxDate = this.studyInfo.is_done ? new Date(this.studyInfo.end_study_time*1000) : new Date(new Date().setDate(new Date().getDate()));

			this.minDate = studyMinDate > this.minDate ? studyMinDate : this.minDate;
		}

		this.fromDate = this.fromDate < this.minDate ? this.minDate : this.fromDate;
		this.toDate = this.toDate > this.maxDate ? this.maxDate : this.toDate;

		this.getDailyChargerUsageReport();
	}

	ngOnChanges(changes){
		// ngOnInit runs right after the first ngOnChanges and already fetches with the correctly clamped
		// dates - fetching here too on that first call races it with the unclamped default dates.
		const isInitialChange = (changes.device && changes.device.firstChange) || (changes.studyInfo && changes.studyInfo.firstChange);

		if((changes.device || changes.studyInfo) && this.device && this.studyInfo && !isInitialChange)
			this.getDailyChargerUsageReport();
	}

	changeDate(field, value) {
		let date = new Date(value);

		if (date < this.minDate)
			date = this.minDate;
		else if (date > this.maxDate)
			date = this.maxDate;

		this[field] = date;

		this.getDailyChargerUsageReport();
	}

	getDailyChargerUsageReport() {
		const zoneDiff	= new Date().getTimezoneOffset() * -1;
		let fromDate:any	= new Date(new Date(this.fromDate).getTime() + (zoneDiff * 60 * 1000));
		let toDate:any = new Date(new Date(this.toDate).getTime() + (zoneDiff * 60 * 1000));
		fromDate = moment(fromDate).utc().startOf('day').unix();
		toDate = moment(toDate).utc().endOf('day').unix();

		const decimalPipe: DecimalPipe = new DecimalPipe("en-US");

		this.deviceService.getDailyDeviceUsageReport(fromDate, toDate, this.device.mac_address, DeviceFactory.types.iotah, 0, this.studyInfo?.id || 0).subscribe((res: any) => {
			const tableData = [];

			for (const day in res) {
				if (res.hasOwnProperty(day)) {
					if (!res[day].day)
						continue;

					tableData.push({
						day: {value: res[day].day},
						in_use_hours: {value:  Math.round((res[day].in_use_second || 0) / 3600).toLocaleString()},
						total_discharged_ah: {value: Math.round((res[day].total_discharged_as || 0) / 3600).toLocaleString()},
						truck_energy_score: {value: Math.round(res[day].truck_energy_score || 0).toLocaleString()},
						total_kwh_used: {value: Math.round((res[day].total_ws_used || 0 )/ (3600 * 1000)).toLocaleString()},
						total_in_use_hours: {value: Math.round((res[day].total_in_use_second || 0) / 3600).toLocaleString()},
						total_travel_hours: {value: Math.round((res[day].total_travel_second || 0) / 3600).toLocaleString()},
						total_lift_hours: {value: Math.round((res[day].total_lift_second || 0) / 3600).toLocaleString()},
						total_idle_hours: {value: Math.round((res[day].total_idle_second || 0) / 3600).toLocaleString()},
						total_off_charging_hours: {value: Math.round((res[day].total_off_charging_second || 0) / 3600).toLocaleString()},
					});
				}
			}

			this.tableData = tableData;
			this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
				customerName: this.currentSite.customer_name,
				siteName: this.currentSite.name,
				deviceSN: this.device.serial_number,
				includeSpacing: false
			});
		});
	}
}
